<template>
  <div class="settings-page">
    <div class="title">
      <div>
        <h2 v-if="lockedDatabaseName">Edit {{ lockedDatabaseName }}</h2>
        <h2 v-else>Add Database</h2>
      </div>
      <div class="actions">
        <BaseLink
          @click="openUrl('https://routine.co/integrations/services/notion')"
        >
          <template #icon> <ExternalIcon /> </template>Help
        </BaseLink>
        <BaseButton
          kind="main"
          type="secondary"
          text="Refresh"
          @click="refresh()"
        >
          <template #icon>
            <RefreshIcon />
          </template>
        </BaseButton>
      </div>
    </div>

    <BaseAlert v-if="integrationError" state="error">
      {{ integrationError }}
    </BaseAlert>

    <section v-else class="databases">
      <div v-if="!lockedDatabaseName">
        <div v-if="pickableDatabases.length > 0" class="entry">
          <label>Select a database to pull from:</label>
          <VSelect
            v-model="selectedDatabase"
            class="routine"
            :clearable="false"
            :options="pickableDatabases"
            :placeholder="'Select ...'"
            :searchable="false"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><ChevronDownIcon /></span>
            </template>
          </VSelect>
        </div>
        <div v-else class="entry">
          <p>All databases are already configured.</p>
        </div>
      </div>

      <template v-if="selectedDatabase !== null">
        <div class="entry">
          <div>
            <label>Select the <code>assignee</code> property: </label>
            <p class="help">
              If "None" is selected, Routine will import all tasks.
            </p>
          </div>
          <VSelect
            v-model="selectedAssigneeProperty"
            class="routine"
            :clearable="false"
            :options="peopleColumns"
            :placeholder="'Select ...'"
            :searchable="false"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><ChevronDownIcon /></span>
            </template>
          </VSelect>
        </div>

        <div class="entry">
          <div>
            <label> Select the <code>status</code> property: </label>
            <p class="help">
              This property will be used to determine if a task has been
              completed or not.
            </p>
          </div>
          <VSelect
            v-model="selectedStatusProperty"
            class="routine"
            :clearable="false"
            :options="statusColumns"
            :placeholder="'Select ...'"
            :searchable="false"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><ChevronDownIcon /></span>
            </template>
          </VSelect>
        </div>

        <template v-if="isSelect || isStatus">
          <!-- Select Import -->
          <div class="entry">
            <div>
              <label>To Do</label>
              <p class="help">
                All the Notion tasks with one of the following statuses will be
                represented as a Routine tasks to do i.e uncompleted.
              </p>
            </div>
            <VSelect
              v-model="selectedTodo"
              class="routine"
              multiple
              :clearable="false"
              :options="pickableStatuses"
              :placeholder="'Select ...'"
              :searchable="false"
            >
              <template #open-indicator="{ attributes }">
                <span v-bind="attributes"><ChevronDownIcon /></span>
              </template>
            </VSelect>
          </div>

          <div class="entry">
            <div>
              <label>Completed</label>
              <p class="help">
                All Notion tasks with one of the following statuses will be
                mapped to a Routine task that is completed.
              </p>
            </div>
            <VSelect
              v-model="selectedCompleted"
              class="routine"
              multiple
              :clearable="false"
              :options="pickableStatuses"
              :placeholder="'Select ...'"
              :searchable="false"
            >
              <template #open-indicator="{ attributes }">
                <span v-bind="attributes"><ChevronDownIcon /></span>
              </template>
            </VSelect>
          </div>

          <div class="entry">
            <div>
              <label>Do not import</label>
              <p class="help">
                All the Notion tasks with one of the following statuses with be
                ignored. No Routine task will ever be created for those
                statuses.
              </p>
            </div>
            <VSelect
              v-model="selectedIgnored"
              class="routine"
              multiple
              :clearable="false"
              :options="pickableStatuses"
              :placeholder="'Select ...'"
              :searchable="false"
            >
              <template #open-indicator="{ attributes }">
                <span v-bind="attributes"><ChevronDownIcon /></span>
              </template>
            </VSelect>
          </div>

          <BaseAlert v-if="numberOfStatusToAssign > 0" state="default">
            Please sort all statuses into the above categories (<strong>{{
              numberOfStatusToAssign
            }}</strong>
            to go).
          </BaseAlert>

          <template v-if="numberOfStatusToAssign === 0">
            <div class="entry">
              <div>
                <label>Complete</label>
                <p class="help">
                  The status to assign in Notion when a task is marked as
                  completed in Routine.
                </p>
              </div>
              <VSelect
                v-model="selectedCompletedInjection"
                class="routine"
                :clearable="false"
                :options="pickableDoneInjectionStatuses"
                :placeholder="'Select ...'"
                :searchable="false"
              >
                <template #open-indicator="{ attributes }">
                  <span v-bind="attributes"><ChevronDownIcon /></span>
                </template>
              </VSelect>
            </div>

            <div class="entry">
              <div>
                <label>Uncomplete</label>
                <p class="help">
                  The status to assign in Notion when a task is marked as
                  incomplete in Routine.
                </p>
              </div>
              <VSelect
                v-model="selectedTodoInjection"
                class="routine"
                :clearable="false"
                :options="pickableTodoInjectionStatuses"
                :placeholder="'Select ...'"
                :searchable="false"
              >
                <template #open-indicator="{ attributes }">
                  <span v-bind="attributes"><ChevronDownIcon /></span>
                </template>
              </VSelect>
            </div>
          </template>

          <div
            v-if="
              numberOfStatusToAssign === 0 &&
              (selectedTodoInjection === null ||
                selectedCompletedInjection === null)
            "
            class="description"
          >
            <span>Please set a value for completed and uncomplete.</span>
          </div>
        </template>
        <template v-if="isCheckbox" />
      </template>
    </section>

    <div class="entry buttons">
      <BaseButton
        v-if="lockedDatabaseName"
        kind="main"
        type="secondary"
        text="Disconnect database"
        tint="red"
        @click="disconnectDatabase()"
      />
      <div class="actions">
        <BaseButton
          kind="main"
          type="tertiary"
          text="Cancel"
          @click="cancel()"
        />
        <BaseButton
          v-if="isSelectionComplete"
          kind="main"
          type="primary"
          :text="lockedDatabaseName ? 'Update' : 'Save'"
          @click="send()"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import * as _ from "lodash-es";
import { computed, ComputedRef, defineComponent, Ref, ref, watch } from "vue";
import { useRoute } from "vue-router";

import {
  IntegrationReport,
  NotionColumn,
  NotionConfig,
  NotionDatabase,
  NotionImport,
  NotionSelectOption,
  NotionStatusGroup,
  NotionStatusOption,
} from "@ctrl/controller-rpc";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseLink from "@/components/base/BaseLink.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import ExternalIcon from "@/components/icons/ExternalIcon.vue";
import RefreshIcon from "@/components/icons/RefreshIcon.vue";
import * as inject from "@/components/inject";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { in_electron } from "@/utils/in";
import { navigateTo } from "@/utils/navigate";
import { openExternalUrl } from "@/utils/open-external-url";
import { property } from "@/utils/unknown";

const NoEntrySentinelId = "__NO_±_VALUE__"; // Hack so null is a selectable value in v-select.

type ColumnValue = {
  label: string;
  id: string;
  name: string;
  group?: NotionStatusGroup;
};

type StoredSelectConfig = {
  completedStatusId: string;
  activeStatusId?: string | null;
  completedStatusEquivalentIds?: Array<string | null>;
  ignoredStatusEquivalentIds?: Array<string | null>;
};

type StoredStatusConfig = {
  completedStatusPropertyId: string;
  activeStatusPropertyId: string;
  completedStatusPropertyEquivalentIds?: string[];
  ignoredStatusPropertyEquivalentIds?: string[];
};

function firstString(value: unknown): string | null {
  const first = Array.isArray(value) ? value[0] : value;
  return typeof first === "string" && first ? first : null;
}

function resolveStatusValues(
  ids: ReadonlyArray<string | null>,
  available: readonly ColumnValue[],
  picked: Set<string>
): ColumnValue[] {
  const values: ColumnValue[] = [];
  for (const id of ids) {
    const resolvedId = id ?? NoEntrySentinelId;
    picked.add(resolvedId);
    const value = available.find((candidate) => candidate.id === resolvedId);
    if (value) values.push(value);
  }
  return values;
}

function nullableStringArray(value: unknown): Array<string | null> | undefined {
  if (!Array.isArray(value)) return undefined;
  const result: Array<string | null> = [];
  for (const item of value) {
    if (typeof item !== "string" && item !== null) return undefined;
    result.push(item);
  }
  return result;
}

function stringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const result: string[] = [];
  for (const item of value) {
    if (typeof item !== "string") return undefined;
    result.push(item);
  }
  return result;
}

function storedSelectConfig(value: unknown): StoredSelectConfig | null {
  const completedStatusId = property(value, "completedStatusId");
  const activeStatusId = property(value, "activeStatusId");
  const completedValues = property(value, "completedStatusEquivalentIds");
  const ignoredValues = property(value, "ignoredStatusEquivalentIds");
  const completedStatusEquivalentIds = nullableStringArray(completedValues);
  const ignoredStatusEquivalentIds = nullableStringArray(ignoredValues);
  if (
    typeof completedStatusId !== "string" ||
    (activeStatusId !== undefined &&
      activeStatusId !== null &&
      typeof activeStatusId !== "string") ||
    (completedValues !== undefined &&
      completedStatusEquivalentIds === undefined) ||
    (ignoredValues !== undefined && ignoredStatusEquivalentIds === undefined)
  ) {
    return null;
  }
  return {
    completedStatusId,
    activeStatusId,
    completedStatusEquivalentIds,
    ignoredStatusEquivalentIds,
  };
}

function storedStatusConfig(value: unknown): StoredStatusConfig | null {
  const completedStatusPropertyId = property(
    value,
    "completedStatusPropertyId"
  );
  const activeStatusPropertyId = property(value, "activeStatusPropertyId");
  const completedValues = property(
    value,
    "completedStatusPropertyEquivalentIds"
  );
  const ignoredValues = property(value, "ignoredStatusPropertyEquivalentIds");
  const completedStatusPropertyEquivalentIds = stringArray(completedValues);
  const ignoredStatusPropertyEquivalentIds = stringArray(ignoredValues);
  if (
    typeof completedStatusPropertyId !== "string" ||
    typeof activeStatusPropertyId !== "string" ||
    (completedValues !== undefined &&
      completedStatusPropertyEquivalentIds === undefined) ||
    (ignoredValues !== undefined &&
      ignoredStatusPropertyEquivalentIds === undefined)
  ) {
    return null;
  }
  return {
    completedStatusPropertyId,
    activeStatusPropertyId,
    completedStatusPropertyEquivalentIds,
    ignoredStatusPropertyEquivalentIds,
  };
}

export default defineComponent({
  components: {
    BaseAlert,
    ChevronDownIcon,
    BaseButton,
    BaseLink,
    ExternalIcon,
    RefreshIcon,
  },
  props: {
    orgId: { type: String, required: true },
  },
  async setup(props) {
    const controller = inject.controller();
    const router = inject.router();
    const route = useRoute();
    const id = computed(() => firstString(route.params["id"]));
    const db_id = computed(() => firstString(route.query["db_id"])); // used to lock the db for editing one particular entry.
    const openUrl = (url: string) => openExternalUrl(url, null, false);
    // data
    var selectedDatabase: Ref<{
      databaseId: string;
      integrationId: string;
      label: string;
      index: number;
    } | null> = ref(null);

    var selectedAssigneeProperty: Ref<
      (NotionColumn & { label: string }) | null
    > = ref(null);

    var selectedStatusProperty: Ref<{
      id: string;
      name: string;
      label: string;
      type: string;
      options: NotionSelectOption[] | NotionStatusOption[];
      index: number;
    } | null> = ref(null);

    var selectedTodo: Ref<
      | {
          id: string;
          name: string;
          label: string;
        }[]
      | null
    > = ref(null);

    var selectedTodoInjection: Ref<{
      id: string;
      name: string;
      label: string;
    } | null> = ref(null);

    var selectedCompleted: Ref<
      | {
          id: string;
          name: string;
          label: string;
        }[]
      | null
    > = ref(null);

    var selectedCompletedInjection: Ref<{
      id: string;
      name: string;
      label: string;
    } | null> = ref(null);

    var selectedIgnored: Ref<
      | {
          id: string;
          name: string;
          label: string;
        }[]
      | null
    > = ref(null);

    const releaser = useReleaser();
    const notionIntegration = await releaser.add(
      bindAsync(id, async (id) => {
        if (id) {
          const i = await controller.integration.get(id);
          return i.map((remote) => (remote?.type === "notion" ? remote : null));
        }
        return releasableRef(null);
      })
    );

    const integrationError = computed(() => {
      if (!in_electron) {
        return null;
        // return "Notion integrations can only be configured from the native App.";
      } else if (!notionIntegration.value) {
        return "Cannot find this Notion integration.";
      } else if (notionIntegration.value.status?.status === "broken") {
        return (
          "This Notion integration is broken (" +
          notionIntegration.value.status?.error +
          ")."
        );
      }
      return null;
    });

    const databases = computed(() => {
      if (
        notionIntegration.value &&
        notionIntegration.value.status?.status === "ready" &&
        notionIntegration.value.databases
      ) {
        return notionIntegration.value.databases.map(
          (d: NotionDatabase, index: number) => ({
            databaseId: d.id,
            integrationId: id.value || "", // id cannot be undefined at this point.
            label: d.title,
            index: index,
          })
        );
      } else {
        return [];
      }
    });

    const pickableDatabases = computed(() => {
      if (db_id.value !== null) {
        return databases.value.filter((db) => db.databaseId === db_id.value);
      } else {
        const existing_configs = new Set(
          notionIntegration.value?.imports?.map((c) => c.databaseId) || []
        );
        return databases.value.filter(
          (db) => !existing_configs.has(db.databaseId)
        );
      }
    });

    const activeDatabase = computed(() => {
      if (
        notionIntegration.value &&
        notionIntegration.value.status &&
        notionIntegration.value.status.status === "ready" &&
        selectedDatabase.value !== null &&
        notionIntegration.value.databases
      ) {
        return (
          notionIntegration.value.databases.find(
            (db) => db.id === selectedDatabase.value?.databaseId
          ) || null
        );
      } else {
        return null;
      }
    });

    const lockedDatabaseName = computed(() => {
      // When DB is specified in the URL
      if (
        db_id.value &&
        activeDatabase.value &&
        activeDatabase.value?.id === db_id.value
      ) {
        return activeDatabase.value.title;
      } else {
        return null;
      }
    });

    const storedConfig = computed(
      () =>
        (selectedDatabase.value &&
          notionIntegration.value?.imports &&
          notionIntegration.value?.imports.find(
            (i) => i.databaseId === selectedDatabase.value?.databaseId
          )) ||
        null
    );

    const storedConfigSelect = computed(() => {
      if (
        !storedConfig.value ||
        selectedStatusProperty.value?.id !== storedConfig.value.statusColumnId
      ) {
        return null;
      }
      return storedSelectConfig(storedConfig.value);
    });

    const storedConfigStatus = computed(() => {
      if (
        !storedConfig.value ||
        selectedStatusProperty.value?.id !== storedConfig.value.statusColumnId
      ) {
        return null;
      }
      return storedStatusConfig(storedConfig.value);
    });

    const isSelect = computed(
      () => selectedStatusProperty.value?.type === "select"
    );
    const isStatus = computed(
      () => selectedStatusProperty.value?.type === "status"
    );
    const isCheckbox = computed(
      () => selectedStatusProperty.value?.type === "checkbox"
    );

    const peopleColumns = computed(() => {
      if (activeDatabase.value !== null) {
        return [
          ...activeDatabase.value.peopleColumns.map((col) => ({
            ...col,
            label: col.name,
          })),
          { id: NoEntrySentinelId, name: "None", label: "None", value: null },
        ];
      } else {
        return [];
      }
    });

    const statusColumns = computed(() => {
      if (activeDatabase.value !== null) {
        return activeDatabase.value.statusColumns.map((v, index) => ({
          id: v.statusColumnId,
          name: v.statusColumnName,
          label: v.statusColumnName,
          type:
            "options" in v ? "select" : "status" in v ? "status" : "checkbox",
          options: "options" in v ? v.options : "status" in v ? v.status : [],
          index: index,
        }));
      } else {
        return [];
      }
    });

    const availableStatuses: ComputedRef<ColumnValue[]> = computed(() => {
      if (
        selectedStatusProperty.value &&
        selectedStatusProperty.value.options.length > 0
      ) {
        return [
          ...selectedStatusProperty.value.options,
          ...(isSelect.value
            ? [{ name: "status not set", id: NoEntrySentinelId }]
            : []),
        ].map((v) => ({
          ...v,
          label: v.name,
        }));
      } else {
        return [];
      }
    });

    const pickableStatuses = computed(() => {
      var alreadyPicked = new Set<string>();
      selectedTodo.value?.forEach((v) => alreadyPicked.add(v.id));
      selectedIgnored.value?.forEach((v) => alreadyPicked.add(v.id));
      selectedCompleted.value?.forEach((v) => alreadyPicked.add(v.id));
      return availableStatuses.value.filter(
        (val) => !alreadyPicked.has(val.id)
      );
    });

    const pickableDoneInjectionStatuses = computed(
      () =>
        (selectedCompleted.value !== null &&
          selectedCompleted.value &&
          selectedCompleted.value.filter((v) => v.id !== NoEntrySentinelId)) ||
        []
    );

    const pickableTodoInjectionStatuses = computed(
      () => (selectedTodo.value !== null && selectedTodo.value) || []
    );

    // auto-selecting database (start of the auto-select chain)
    function reset() {
      // console.log("WATCHER: reset db selection");
      if (
        pickableDatabases.value.length > 0 &&
        selectedDatabase.value === null
      ) {
        if (db_id.value) {
          // console.log("-> Reset to selected");
          selectedDatabase.value =
            pickableDatabases.value.find(
              (db) => db.databaseId === db_id.value
            ) || null;
        } else {
          // console.log("-> Reset to first");
          selectedDatabase.value = pickableDatabases.value[0];
        }
      } else if (pickableDatabases.value.length === 0) {
        // console.log("-> No possible selection");
        selectedDatabase.value = null;
      } else {
        // console.log("-> No need to update");
      }
    }

    releaser.defer(
      watch(databases, () => {
        // console.log("WATCHER: database");
        reset();
      })
    );

    releaser.defer(
      watch(peopleColumns, (people) => {
        if (people.length > 0) {
          if (storedConfig.value) {
            if (
              storedConfig.value.assigneeColumnId === null ||
              storedConfig.value.assigneeColumnId === undefined ||
              storedConfig.value.assigneeId === null ||
              storedConfig.value.assigneeId === undefined
            ) {
              selectedAssigneeProperty.value =
                people.find((p) => p.id === NoEntrySentinelId) || null;
            } else {
              const configSelectedId = storedConfig.value.assigneeId;
              selectedAssigneeProperty.value =
                people.find((p) => p.id === configSelectedId) || people[0];
            }
          } else {
            selectedAssigneeProperty.value = people[0];
          }
        } else {
          selectedAssigneeProperty.value = null;
        }
      })
    );

    releaser.defer(
      watch(statusColumns, (cols) => {
        // console.log("WATCHER: status properties");
        if (cols.length > 0) {
          const storedColumn =
            (storedConfig.value &&
              statusColumns.value &&
              statusColumns.value.find(
                (col) => col.id === storedConfig.value?.statusColumnId
              )) ||
            null;

          if (
            selectedStatusProperty.value !== null &&
            cols.find((c) => c.id === selectedStatusProperty.value?.id)
          ) {
            // console.log("-> noting todo");
            return;
          } else if (storedColumn !== null) {
            selectedStatusProperty.value = storedColumn;
          } else {
            selectedStatusProperty.value = cols[0];
          }
        } else {
          selectedStatusProperty.value = null;
        }
      })
    );

    releaser.defer(
      watch(selectedStatusProperty, (prop, oldProp) => {
        if (prop?.id !== oldProp?.id || selectedTodo.value?.length === 0) {
          if (storedConfigSelect.value || storedConfigStatus.value) {
            // console.log("WATCHER: status property -> Fill");
            // sorting status values into todo-complete-ignore
            const picked = new Set<string>();
            if (storedConfigSelect.value?.completedStatusEquivalentIds) {
              selectedCompleted.value = resolveStatusValues(
                storedConfigSelect.value.completedStatusEquivalentIds,
                availableStatuses.value,
                picked
              );
            } else if (
              storedConfigStatus.value?.completedStatusPropertyEquivalentIds
            ) {
              selectedCompleted.value = resolveStatusValues(
                storedConfigStatus.value.completedStatusPropertyEquivalentIds,
                availableStatuses.value,
                picked
              );
            } else {
              selectedCompleted.value = [];
            }

            if (storedConfigSelect.value?.ignoredStatusEquivalentIds) {
              selectedIgnored.value = resolveStatusValues(
                storedConfigSelect.value.ignoredStatusEquivalentIds,
                availableStatuses.value,
                picked
              );
            } else if (
              storedConfigStatus.value?.ignoredStatusPropertyEquivalentIds
            ) {
              selectedIgnored.value = resolveStatusValues(
                storedConfigStatus.value.ignoredStatusPropertyEquivalentIds,
                availableStatuses.value,
                picked
              );
            } else {
              selectedIgnored.value = [];
            }

            // TODO is not stored, it is everything else.
            selectedTodo.value = availableStatuses.value.filter(
              (v) => !picked.has(v.id)
            );

            // setting up chosen todo injection
            if (storedConfigSelect.value?.activeStatusId) {
              selectedTodoInjection.value =
                selectedTodo.value.find(
                  (v) =>
                    v.id === storedConfigSelect.value?.activeStatusId ||
                    (v.id === NoEntrySentinelId &&
                      storedConfigSelect.value?.activeStatusId === null)
                ) || null;
            } else if (storedConfigStatus.value?.activeStatusPropertyId) {
              selectedTodoInjection.value =
                selectedTodo.value.find(
                  (v) =>
                    v.id === storedConfigStatus.value?.activeStatusPropertyId
                ) || null;
            } else {
              selectedTodoInjection.value = null;
            }

            // setting up chosen done injection
            if (storedConfigSelect.value?.completedStatusId) {
              selectedCompletedInjection.value =
                selectedCompleted.value.find(
                  (v) => v.id === storedConfigSelect.value?.completedStatusId
                ) || null;
            } else if (storedConfigStatus.value?.completedStatusPropertyId) {
              selectedCompletedInjection.value =
                selectedCompleted.value.find(
                  (v) =>
                    v.id === storedConfigStatus.value?.completedStatusPropertyId
                ) || null;
            } else {
              selectedCompletedInjection.value = null;
            }
          } else {
            // console.log("WATCHER: status property -> Clear");
            if (isStatus.value) {
              selectedTodo.value = pickableStatuses.value.filter(
                (v) =>
                  "group" in v &&
                  (v.group === "todo" || v.group === "in_progress")
              );
              selectedCompleted.value = pickableStatuses.value.filter(
                (v) => "group" in v && v.group === "completed"
              );
              selectedIgnored.value = [];
            } else {
              selectedTodo.value = [];
              selectedIgnored.value = [];
              selectedCompleted.value = [];
              selectedTodoInjection.value = null;
              selectedCompletedInjection.value = null;
            }
          }
        }
      })
    );

    releaser.defer(
      watch(selectedTodo, (todos) => {
        // console.log("WATCHER: selectedTodo");
        if (selectedTodoInjection.value === null) {
          if (todos && todos.length > 0) {
            selectedTodoInjection.value = todos[0];
          }
        } else if (
          todos?.find((todo) => todo.id === selectedTodoInjection.value?.id) ===
          undefined
        ) {
          selectedTodoInjection.value = null;
        }
      })
    );

    releaser.defer(
      watch(selectedCompleted, (complete) => {
        // console.log("WATCHER: selectedCompleted");
        if (selectedCompletedInjection.value === null) {
          if (complete && complete.length > 0) {
            selectedCompletedInjection.value = complete[0];
          }
        } else if (
          complete?.find(
            (comp) => comp.id === selectedCompletedInjection.value?.id
          ) === undefined
        ) {
          selectedCompletedInjection.value = null;
        }
      })
    );

    const numberOfStatusToAssign = computed(
      () => pickableStatuses.value && pickableStatuses.value.length
    );

    const isSelectionComplete = computed(
      () =>
        isCheckbox.value ||
        ((isSelect.value || isStatus.value) &&
          numberOfStatusToAssign.value === 0 &&
          selectedCompletedInjection.value !== null &&
          selectedTodoInjection.value !== null &&
          selectedAssigneeProperty.value !== null)
    );

    // form functions
    function cancel() {
      navigateTo(RouteNames.SettingsIntegrationsNotion, router, {
        params: { orgId: props.orgId },
      });
    }

    function refresh() {
      if (id.value) {
        controller.integration.refresh(id.value);
      }
    }

    async function getJsSettings(): Promise<
      | (NotionConfig<IntegrationReport> & {
          type: "notion";
        })
      | null
    > {
      if (
        !isSelectionComplete.value ||
        notionIntegration.value === null ||
        activeDatabase.value === null ||
        selectedStatusProperty.value === null ||
        !notionIntegration.value
      )
        return null;

      const self = notionIntegration.value.self;
      const secret = notionIntegration.value.secret;
      const people = notionIntegration.value.people;
      const databases = notionIntegration.value.databases;
      var importsToKeep =
        notionIntegration.value?.imports?.filter(
          (c) => c.databaseId !== activeDatabase.value?.id
        ) || [];

      const importId = await controller.integration.notion.generateImportId();

      const base = {
        id: importId,
        databaseId: activeDatabase.value.id,
        statusColumnId: selectedStatusProperty.value.id,
        assigneeColumnId:
          (selectedAssigneeProperty.value &&
            selectedAssigneeProperty.value.id !== NoEntrySentinelId &&
            selectedAssigneeProperty.value.id) ||
          undefined,
        assigneeId: self,
      };

      if (isSelect.value && selectedCompletedInjection.value) {
        // Making a Select import configuration

        const importConfig: NotionImport = {
          ...base,
          completedStatusId: selectedCompletedInjection.value.id,
          activeStatusId: selectedTodoInjection.value?.id,
          completedStatusEquivalentIds: selectedCompleted.value?.map((v) => {
            if (v.id === NoEntrySentinelId) {
              return null;
            } else {
              return v.id;
            }
          }),
          ignoredStatusEquivalentIds: selectedIgnored.value?.map((v) => {
            if (v.id === NoEntrySentinelId) {
              return null;
            } else {
              return v.id;
            }
          }),
        };
        importsToKeep.push(importConfig);
      } else if (isStatus.value) {
        // Making a Status import configuration
        if (
          selectedCompletedInjection.value === null ||
          selectedTodoInjection.value === null
        ) {
          return null;
        }
        const importConfig: NotionImport = {
          ...base,
          completedStatusPropertyId: selectedCompletedInjection.value.id,
          activeStatusPropertyId: selectedTodoInjection.value?.id,
          completedStatusPropertyEquivalentIds: selectedCompleted.value?.map(
            (v) => v.id
          ),
          ignoredStatusPropertyEquivalentIds: selectedIgnored.value?.map(
            (v) => v.id
          ),
        };
        importsToKeep.push(importConfig);
      } else if (isCheckbox.value) {
        // Making a Checkbox import configuration
        const importConfig: NotionImport = base;
        importsToKeep.push(importConfig);
      } else {
        // impossible to reach
        return null;
      }

      return {
        type: "notion",
        self,
        secret,
        imports: importsToKeep,
        people,
        databases,
        status: { status: "synchronizing" },
      };
    }

    async function send() {
      const config = await getJsSettings();
      const integrationId = id.value;
      if (!config || !integrationId) {
        return; // unreachable by normal means
      }
      try {
        controller.integration.update(integrationId, config);
      } catch (e) {
        controller.err("Update failed:" + e);
      } finally {
        navigateTo(RouteNames.SettingsIntegrationsNotion, router, {
          params: { orgId: props.orgId },
        });
      }
    }

    function disconnectDatabase(): void {
      if (
        !notionIntegration.value ||
        !id.value ||
        notionIntegration.value.status?.status !== "ready" ||
        notionIntegration.value.imports === undefined
      ) {
        return;
      }

      const notionImport = notionIntegration.value?.imports?.find(
        (c: NotionImport) => c.databaseId === activeDatabase.value?.id
      );

      if (notionImport) {
        try {
          const newImports = _.pull(
            notionIntegration.value.imports,
            notionImport
          );
          controller.integration.update(id.value, {
            type: "notion",
            self: notionIntegration.value.self,
            secret: notionIntegration.value.secret,
            imports: newImports,
            people: notionIntegration.value.people,
            databases: notionIntegration.value.databases,
            status: { status: "synchronizing" },
          });
        } catch (e) {
          controller.err("Disconnect failed:" + e);
        } finally {
          navigateTo(RouteNames.SettingsIntegrationsNotion, router, {
            params: { orgId: props.orgId },
          });
        }
      }
    }

    // Context
    return {
      cancel,
      disconnectDatabase,
      integrationError,
      isCheckbox,
      isSelect,
      isSelectionComplete,
      isStatus,
      lockedDatabaseName,
      notionIntegration,
      numberOfStatusToAssign,
      openUrl,
      peopleColumns,
      pickableDatabases,
      pickableDoneInjectionStatuses,
      pickableStatuses,
      pickableTodoInjectionStatuses,
      refresh,
      reset,
      selectedAssigneeProperty,
      selectedCompleted,
      selectedCompletedInjection,
      selectedDatabase,
      selectedIgnored,
      selectedStatusProperty,
      selectedTodo,
      selectedTodoInjection,
      send,
      statusColumns,
    };
  },
  mounted() {
    if (this.reset) this.reset(); // This allows for components to be re-used.
  },
});
</script>

<style lang="scss" scoped>
@use "../../styles/settings.scss";

.entry {
  > div {
    display: flex;
    flex-direction: column;
  }

  .buttons {
    flex-direction: row;
  }

  &.buttons {
    justify-content: flex-end;

    .actions {
      flex-direction: row;
    }
  }
}
</style>
